/**
 * Media API functions for library, tagging, and processing
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// Types
export interface MediaTag {
  id: number;
  media_id: number;
  person_id: number | null;
  connected_person_id: number | null;
  tag_name: string | null;  // Text-only tag for unlinked individuals
  person_name: string | null;  // Display name (computed)
  person_type: "tree" | "connected" | "name_only";
  region_x: number | null;
  region_y: number | null;
  region_width: number | null;
  region_height: number | null;
  created_at: string;
}

export interface LocationSummary {
  id: number;
  name: string;
}

export interface MemorySummary {
  id: number;
  name: string;
}

export interface MediaItem {
  id: number;
  tree_id: number | null;  // Nullable - media is tree-agnostic
  person_id: number | null;
  event_id: number | null;
  filename: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  media_type: string;
  caption: string | null;
  description: string | null;
  is_profile: boolean;
  date_taken: string | null;
  // Enhanced metadata
  estimated_date: string | null;
  estimated_date_text: string | null;
  location: string | null;
  photographer: string | null;
  created_at: string;
  // Processing fields
  transcription: string | null;
  ocr_text: string | null;
  processing_status: string;
  processing_error: string | null;
  // Tags
  tags: MediaTag[];
  locations: LocationSummary[];
  memories: MemorySummary[];
}

export interface MediaLibraryResponse {
  items: MediaItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface MediaLibraryFilters {
  page?: number;
  pageSize?: number;
  mediaType?: string;
  taggedPersonId?: number;
  taggedConnectedPersonId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  processingStatus?: string;
  vaultId?: number;
}

export interface CreateTagData {
  person_id?: number;
  connected_person_id?: number;
  tag_name?: string;  // Text-only tag for unlinked individuals
  region_x?: number;
  region_y?: number;
  region_width?: number;
  region_height?: number;
}

export interface UpdateMediaData {
  caption?: string;
  description?: string;
  is_profile?: boolean;
  date_taken?: string;
  estimated_date?: string;
  estimated_date_text?: string;
  location?: string;
  photographer?: string;
  media_type?: string;  // photo, document, audio, video, other
  ocr_text?: string;  // Manually corrected OCR text
  transcription?: string;  // Manually corrected transcription
}

// Media type options for display
export const MEDIA_TYPE_OPTIONS = [
  { value: "photo", label: "Photo", description: "Images of people/places (face tagging enabled)" },
  { value: "document", label: "Document", description: "Scanned documents, certificates, letters" },
  { value: "audio", label: "Audio", description: "Voice recordings, interviews" },
  { value: "video", label: "Video", description: "Video recordings" },
  { value: "other", label: "Other", description: "Miscellaneous files" },
] as const;

// =============================================================================
// LIBRARY
// =============================================================================

/**
 * Build query parameters for media library filters
 */
function buildMediaLibraryParams(filters?: MediaLibraryFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.page) params.set("page", String(filters.page));
  if (filters?.pageSize) params.set("page_size", String(filters.pageSize));
  if (filters?.mediaType) params.set("media_type", filters.mediaType);
  if (filters?.taggedPersonId) params.set("tagged_person_id", String(filters.taggedPersonId));
  if (filters?.taggedConnectedPersonId) params.set("tagged_connected_person_id", String(filters.taggedConnectedPersonId));
  if (filters?.search) params.set("search", filters.search);
  if (filters?.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters?.dateTo) params.set("date_to", filters.dateTo);
  if (filters?.processingStatus) params.set("processing_status", filters.processingStatus);
  if (filters?.vaultId) params.set("vault_id", String(filters.vaultId));
  return params;
}

/**
 * Get global media library (all media) with filtering
 */
export async function getGlobalMediaLibrary(
  token: string,
  filters?: MediaLibraryFilters
): Promise<MediaLibraryResponse> {
  const params = buildMediaLibraryParams(filters);

  const url = `${API_URL}/media/library?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch media library");
  }

  return response.json();
}

/**
 * Get single media item info
 */
export async function getMediaInfo(
  mediaId: number,
  token: string
): Promise<MediaItem> {
  const response = await fetch(`${API_URL}/media/${mediaId}/info`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch media info");
  }

  return response.json();
}

/**
 * Get media library for a tree with filtering
 * Media is filtered by tags linking to persons in the tree
 */
export async function getMediaLibrary(
  treeId: number,
  token: string,
  filters?: MediaLibraryFilters
): Promise<MediaLibraryResponse> {
  const params = buildMediaLibraryParams(filters);

  const url = `${API_URL}/media/library/${treeId}?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch media library");
  }

  return response.json();
}

/**
 * Update media metadata
 */
export async function updateMedia(
  mediaId: number,
  data: UpdateMediaData,
  token: string
): Promise<MediaItem> {
  const response = await fetch(`${API_URL}/media/${mediaId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to update media" }));
    throw new Error(error.detail || "Failed to update media");
  }

  return response.json();
}

/**
 * Get media for a connected person
 */
export async function getConnectedPersonMedia(
  connectedPersonId: number,
  token: string
): Promise<MediaItem[]> {
  const response = await fetch(`${API_URL}/media/connected-person/${connectedPersonId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch connected person media");
  }

  return response.json();
}

// =============================================================================
// TAGGING
// =============================================================================

/**
 * Get tags for a media item
 */
export async function getMediaTags(
  mediaId: number,
  token: string
): Promise<MediaTag[]> {
  const response = await fetch(`${API_URL}/media/${mediaId}/tags`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch media tags");
  }

  return response.json();
}

/**
 * Add a tag to a media item
 */
export async function createMediaTag(
  mediaId: number,
  data: CreateTagData,
  token: string
): Promise<MediaTag> {
  const response = await fetch(`${API_URL}/media/${mediaId}/tags`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to create tag" }));
    throw new Error(error.detail || "Failed to create tag");
  }

  return response.json();
}

/**
 * Update a tag
 */
export async function updateMediaTag(
  mediaId: number,
  tagId: number,
  data: CreateTagData,
  token: string
): Promise<MediaTag> {
  const response = await fetch(`${API_URL}/media/${mediaId}/tags/${tagId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to update tag");
  }

  return response.json();
}

/**
 * Delete a tag
 */
export async function deleteMediaTag(
  mediaId: number,
  tagId: number,
  token: string
): Promise<void> {
  const response = await fetch(`${API_URL}/media/${mediaId}/tags/${tagId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete tag");
  }
}

export async function tagMediaToPerson(
  mediaId: number,
  personId: number,
  token: string
): Promise<MediaTag> {
  const response = await fetch(
    `${API_URL}/media/${mediaId}/tags/person?person_id=${personId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to tag person to media");
  }

  return await response.json();
}

export async function tagMediaToConnectedPerson(
  mediaId: number,
  connectedPersonId: number,
  token: string
): Promise<MediaTag> {
  const response = await fetch(
    `${API_URL}/media/${mediaId}/tags/connected-person?connected_person_id=${connectedPersonId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to tag connected person to media");
  }

  return await response.json();
}

// =============================================================================
// PROCESSING
// =============================================================================

/**
 * Get processing status of a media item
 */
export async function getProcessingStatus(
  mediaId: number,
  token: string
): Promise<{
  media_id: number;
  media_type: string;
  processing_status: string;
  processing_error: string | null;
  has_transcription: boolean;
  has_ocr_text: boolean;
}> {
  const response = await fetch(`${API_URL}/media/${mediaId}/processing-status`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to get processing status");
  }

  return response.json();
}

/**
 * Trigger processing for a media item
 */
export async function triggerProcessing(
  mediaId: number,
  token: string,
  force: boolean = false
): Promise<{
  message: string;
  processing_status: string;
}> {
  const params = new URLSearchParams();
  if (force) params.set("force", "true");

  const response = await fetch(`${API_URL}/media/${mediaId}/process?${params.toString()}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to trigger processing" }));
    throw new Error(error.detail || "Failed to trigger processing");
  }

  return response.json();
}

// =============================================================================
// FILENAME CONFLICT CHECK
// =============================================================================

export interface FilenameConflict {
  filename: string;
  existing_media_id: number;
  existing_caption: string | null;
  existing_created_at: string;
}

export interface FilenameCheckResponse {
  conflicts: FilenameConflict[];
  available: string[];
}

/**
 * Check if filenames already exist in the media library for the given vault.
 * Use before batch uploads to handle duplicates gracefully (per-vault).
 */
export async function checkFilenames(
  vaultId: number,
  filenames: string[],
  token: string
): Promise<FilenameCheckResponse> {
  const response = await fetch(`${API_URL}/media/check-filenames`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ vault_id: vaultId, filenames }),
  });

  if (!response.ok) {
    throw new Error("Failed to check filenames");
  }

  return response.json();
}

// =============================================================================
// IMAGE EDITING (ROTATION & CROPPING)
// =============================================================================

/**
 * Rotate an image by 90, 180, or 270 degrees clockwise
 */
export async function rotateImage(
  mediaId: number,
  degrees: 90 | 180 | 270,
  token: string
): Promise<MediaItem> {
  const response = await fetch(`${API_URL}/media/${mediaId}/rotate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ degrees }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to rotate image" }));
    throw new Error(error.detail || "Failed to rotate image");
  }

  return response.json();
}

export interface CropRegion {
  x: number;      // Left edge as percentage (0-100)
  y: number;      // Top edge as percentage (0-100)
  width: number;  // Width as percentage (0-100)
  height: number; // Height as percentage (0-100)
}

/**
 * Crop an image to the specified region
 * Coordinates are percentages (0-100) of the original image
 */
export async function cropImage(
  mediaId: number,
  crop: CropRegion,
  token: string
): Promise<MediaItem> {
  const response = await fetch(`${API_URL}/media/${mediaId}/crop`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(crop),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to crop image" }));
    throw new Error(error.detail || "Failed to crop image");
  }

  return response.json();
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Get media URL for display
 */
export function getMediaUrl(mediaId: number): string {
  return `${API_URL}/media/${mediaId}`;
}

/**
 * Download media file as blob (authenticated). Use for Download button so the
 * request includes the Bearer token; direct links cannot send headers.
 */
export async function downloadMediaBlob(
  mediaId: number,
  token: string
): Promise<Blob> {
  const response = await fetch(`${API_URL}/media/${mediaId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: "Download failed" }));
    throw new Error(typeof err.detail === "string" ? err.detail : "Download failed");
  }
  return response.blob();
}

/**
 * Get authenticated media URL (for embedding in img tags)
 * Note: For actual authenticated access, use the useMediaUrl hook instead
 */
export function getAuthenticatedMediaUrl(mediaId: number, _token: string): string {
  // For most cases, we'll use the getMediaUrl and pass token in headers
  // But for img src, we need a different approach (see useMediaUrl hook)
  return `${API_URL}/media/${mediaId}`;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Get media type icon name
 */
export function getMediaTypeIcon(mediaType: string): string {
  switch (mediaType) {
    case "photo":
      return "image";
    case "document":
      return "file-text";
    case "audio":
      return "music";
    case "video":
      return "video";
    default:
      return "file";
  }
}

/**
 * Get media type display label
 */
export function getMediaTypeLabel(mediaType: string): string {
  switch (mediaType) {
    case "photo":
      return "Photo";
    case "document":
      return "Document";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    default:
      return "Other";
  }
}

/**
 * Check if media type supports processing
 */
export function supportsProcessing(mediaType: string): boolean {
  return ["audio", "video", "document"].includes(mediaType);
}

/**
 * Get processing status display
 */
export function getProcessingStatusDisplay(status: string): {
  label: string;
  color: string;
} {
  switch (status) {
    case "pending":
      return { label: "Pending", color: "yellow" };
    case "processing":
      return { label: "Processing...", color: "blue" };
    case "completed":
      return { label: "Processed", color: "green" };
    case "failed":
      return { label: "Failed", color: "red" };
    case "skipped":
      return { label: "Not Processed", color: "gray" };
    default:
      return { label: status, color: "gray" };
  }
}
